<template>
  <Teleport to="body">
    <div v-if="visible" class="ic-overlay" @click.self="$emit('cancel')">
      <div class="ic-dialog">
        <div class="ic-header">
          <span class="ic-title">图片裁剪</span>
          <div class="ic-ratio-group">
            <button class="ic-ratio-btn" :class="{ active: ratioMode === 'free' }" @click="setRatio('free')">自由</button>
            <button class="ic-ratio-btn" :class="{ active: ratioMode === '1:1' }" @click="setRatio('1:1')">1:1</button>
            <button class="ic-ratio-btn" :class="{ active: ratioMode === '3:4' }" @click="setRatio('3:4')">3:4</button>
          </div>
          <button class="ic-close" @click="$emit('cancel')">&times;</button>
        </div>
        <div class="ic-body">
          <div v-if="loading" class="ic-loading">正在加载图片...</div>
          <img v-show="!loading" ref="imgRef" :src="resolvedSrc" />
        </div>
        <div class="ic-footer">
          <button class="ic-btn ic-btn-cancel" @click="$emit('cancel')">取消</button>
          <button class="ic-btn ic-btn-restore" @click="$emit('restore')">复原</button>
          <button class="ic-btn ic-btn-confirm" @click="handleConfirm">确认裁剪</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, watch, nextTick } from 'vue'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

const RATIO_MAP = { 'free': NaN, '1:1': 1, '3:4': 3 / 4 }

const props = defineProps({
  visible: Boolean,
  imageSrc: String
})

const emit = defineEmits(['confirm', 'cancel', 'restore'])

const imgRef = ref(null)
const resolvedSrc = ref('')
const loading = ref(false)
const ratioMode = ref('free')
let cropperInstance = null

const destroyCropper = () => {
  if (cropperInstance) {
    cropperInstance.destroy()
    cropperInstance = null
  }
}

const resolveSrc = async (src) => {
  if (!src) return ''
  if (src.startsWith('data:')) return src
  if (/^[A-Za-z0-9+/=]+$/.test(src) && src.length > 100) return `data:image/png;base64,${src}`
  try {
    const res = await fetch('/api/image-to-base64', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: src })
    })
    const data = await res.json()
    return data.imageBase64 || ''
  } catch (e) {
    console.error('[Cropper] proxy failed:', e)
    return src
  }
}

const buildCropper = (img) => {
  destroyCropper()
  img.style.maxWidth = 'none'
  img.style.display = 'block'
  img.style.width = '100%'
  img.style.height = '100%'
  img.style.objectFit = 'contain'
  cropperInstance = new Cropper(img, {
    viewMode: 1,
    dragMode: 'crop',
    aspectRatio: RATIO_MAP[ratioMode.value],
    autoCropArea: 0.8,
    responsive: true,
    background: true,
    modal: true,
    guides: true,
    center: true,
    highlight: true,
    cropBoxMovable: true,
    cropBoxResizable: true,
    toggleDragModeOnDblclick: false,
    ready() {
      console.log('[Cropper] ready')
    }
  })
}

watch(() => props.visible, async (val) => {
  if (!val) { destroyCropper(); return }
  ratioMode.value = 'free'

  const src = await resolveSrc(props.imageSrc)
  if (!src) return
  resolvedSrc.value = src

  await nextTick()
  const img = imgRef.value
  if (!img) return

  const init = () => buildCropper(img)
  if (img.complete && img.naturalWidth > 0) init()
  else img.onload = init
})

const setRatio = (mode) => {
  ratioMode.value = mode
  if (!cropperInstance) return
  cropperInstance.setAspectRatio(RATIO_MAP[mode])
}

const handleConfirm = () => {
  if (!cropperInstance) return
  const canvas = cropperInstance.getCroppedCanvas({
    maxWidth: 2048,
    maxHeight: 2048,
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high'
  })
  if (!canvas) return
  emit('confirm', canvas.toDataURL('image/png'))
}
</script>

<style>
.ic-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ic-dialog {
  background: #fff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
.ic-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e6eb;
}
.ic-title { font-size: 15px; font-weight: 600; color: #1d2129; white-space: nowrap; }
.ic-ratio-group {
  display: flex;
  gap: 4px;
  background: #f2f3f5;
  border-radius: 6px;
  padding: 3px;
}
.ic-ratio-btn {
  padding: 4px 14px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  color: #4e5969;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.ic-ratio-btn:hover { color: #165dff; }
.ic-ratio-btn.active {
  background: #fff;
  color: #165dff;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.ic-close { background: none; border: none; font-size: 22px; color: #86909c; cursor: pointer; line-height: 1; padding: 0 4px; margin-left: auto; }
.ic-close:hover { color: #1d2129; }
.ic-body {
  width: min(65vh, 80vw);
  height: min(65vh, 80vw);
  background: #f0f0f0;
  overflow: hidden;
}
.ic-body img { display: block; }
.ic-loading { color: #86909c; font-size: 13px; }
.ic-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #e5e6eb;
}
.ic-btn { padding: 7px 20px; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; transition: background 0.2s; }
.ic-btn-cancel { background: #f2f3f5; color: #4e5969; }
.ic-btn-cancel:hover { background: #e5e6eb; }
.ic-btn-restore { background: #fff7e8; color: #ff7d00; border: 1px solid #ffe4ba; }
.ic-btn-restore:hover { background: #fff1d6; }
.ic-btn-confirm { background: #165dff; color: #fff; }
.ic-btn-confirm:hover { background: #0e4ddb; }
</style>
